import { WarningCircleIcon } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import { useEnforcedBudget } from '~/hooks/queries/use-budget';
import { TopUpDialog } from './budget-card';
import { describeReset, formatBlocks } from './budget-meter';

/**
 * Shown in the conversation when the person's compute budget is spent
 * (FRONT-1194).
 *
 * Deliberately a card in the thread rather than a toast: the turn genuinely
 * cannot run, that state persists until the period resets or someone raises
 * the budget, and a toast that disappears leaves the person typing into a void.
 *
 * The wording is the tenant's own where they wrote any — their copy names who
 * to contact, which ours never could. Each field falls back separately because
 * a workspace may set a message but no headline.
 */
export function BudgetBlockedCard() {
  const budget = useEnforcedBudget();
  if (!budget?.limitReached) return null;

  const reset = describeReset(budget.periodResetsAt);
  const headline = budget.copy.headline ?? 'You’ve used your compute budget';
  const body = budget.copy.message ?? budget.copy.banner;
  // Dev has a terse message and a fuller banner; show both when they differ so
  // the useful one always reaches the reader.
  const extra =
    budget.copy.banner && budget.copy.banner !== body ? budget.copy.banner : null;

  return (
    <div className="rounded-lg border border-destructive/40 border-l-[3px] border-l-destructive bg-card p-4 flex gap-3">
      <WarningCircleIcon size={18} className="text-destructive mt-0.5 shrink-0" />
      <div className="flex flex-col gap-2 min-w-0">
        <p className="text-sm font-semibold text-foreground">{headline}</p>
        {body && <p className="text-sm text-muted-foreground">{body}</p>}
        {extra && <p className="text-xs text-muted-foreground/80">{extra}</p>}
        <p className="text-xs text-muted-foreground tabular-nums">
          {formatBlocks(budget.used)} of {formatBlocks(budget.limit)} used
          {reset ? ` · ${reset}` : ''}
        </p>
        {budget.topUp.enabled && (
          <div className="pt-1">
            <TopUpDialog
              budget={budget}
              trigger={<Button size="sm">Request a top-up</Button>}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Replaces the composer while the budget is spent. Returns null otherwise, so
 * callers can render it unconditionally next to the real composer.
 */
export function BudgetComposerLock() {
  const budget = useEnforcedBudget();
  if (!budget?.limitReached) return null;

  const reset = describeReset(budget.periodResetsAt);
  return (
    <div className="rounded-lg border border-border bg-muted/60 px-3.5 py-3 text-sm text-muted-foreground">
      Chat resumes when your budget {reset ?? 'resets'} or your workspace raises it.
    </div>
  );
}

/** True when the composer must be swapped for the lock. */
export function useBudgetBlocked(): boolean {
  return useEnforcedBudget()?.limitReached === true;
}
